import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Close, ShieldOutlined, LockReset } from "@mui/icons-material";

function OtpPopup({ open, onClose, mobileNumber, onVerify, onResend }) {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [timer, setTimer] = useState(30);
  const [loading, setLoading] = useState(false);
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  // Countdown timer for Resend OTP
  useEffect(() => {
    let interval;
    if (open && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [open, timer]);

  // Reset states when dialog opens/closes
  useEffect(() => {
    if (open) {
      setOtp(["", "", "", ""]);
      setTimer(30);
      setLoading(false);
      // Auto-focus first input
      setTimeout(() => {
        if (inputRefs[0].current) {
          inputRefs[0].current.focus();
        }
      }, 100);
    }
  }, [open]);

  const handleChange = (index, value) => {
    // Only allow numbers
    const numericValue = value.replace(/\D/g, "");
    if (!numericValue) return;

    const newOtp = [...otp];
    // If value length is > 1 (e.g. pasted or fast typed), take the last char
    newOtp[index] = numericValue.substring(numericValue.length - 1);
    setOtp(newOtp);

    // Auto-focus next input
    if (index < 3 && newOtp[index] !== "") {
      inputRefs[index + 1].current.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];
      if (otp[index] === "") {
        // Focus previous input on backspace if current is empty
        if (index > 0) {
          inputRefs[index - 1].current.focus();
          newOtp[index - 1] = "";
          setOtp(newOtp);
        }
      } else {
        // Clear current input
        newOtp[index] = "";
        setOtp(newOtp);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");
    if (pastedData.length >= 4) {
      const newOtp = pastedData.slice(0, 4).split("");
      setOtp(newOtp);
      inputRefs[3].current.focus();
    }
  };

  const handleVerify = async () => {
    const otpValue = otp.join("");
    if (otpValue.length < 4) {
      return;
    }
    setLoading(true);
    try {
      await onVerify(otpValue);
    } catch (err) {
      // Handled in parent
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (timer > 0) return;
    setLoading(true);
    try {
      await onResend();
      setTimer(30);
      setOtp(["", "", "", ""]);
      inputRefs[0].current.focus();
    } catch (err) {
      // Handled in parent
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "28px",
          border: "1px solid rgba(15, 23, 42, 0.08)",
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.15)",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(12px)",
          p: 3,
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <IconButton onClick={onClose} size="small" sx={{ color: "#64748b" }}>
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, textAlign: "center" }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 64,
            height: 64,
            borderRadius: "20px",
            background: "linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(21, 101, 192, 0.1) 100%)",
            color: "#1565c0",
            mb: 2.5,
          }}
        >
          <ShieldOutlined sx={{ fontSize: 32 }} />
        </Box>

        <Typography
          variant="h5"
          sx={{
            // fontWeight: 800,
            color: "#0f172a",
            letterSpacing: "-0.02em",
            mb: 1,
          }}
        >
          Verify OTP
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#64748b",
            lineHeight: 1.6,
            maxWidth: 300,
            mx: "auto",
            mb: 4,
          }}
        >
          We've sent a 4-digit verification code to
          <Box component="span" sx={{ fontWeight: 700, color: "#0f172a", ml: 0.5 }}>
            {mobileNumber}
          </Box>
        </Typography>

        {/* 4 Digit Input Fields */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            mb: 4,
          }}
        >
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={inputRefs[index]}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              style={{
                width: "60px",
                height: "64px",
                fontSize: "1.75rem",
                fontWeight: "750",
                textAlign: "center",
                borderRadius: "16px",
                border: "2px solid rgba(148, 163, 184, 0.3)",
                backgroundColor: "#fff",
                color: "#0f172a",
                outline: "none",
                transition: "all 0.25s ease",
                boxShadow: "0 2px 8px rgba(15, 23, 42, 0.02)",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#1565c0";
                e.target.style.boxShadow = "0 0 0 4px rgba(25, 118, 210, 0.12)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "rgba(148, 163, 184, 0.3)";
                e.target.style.boxShadow = "none";
              }}
            />
          ))}
        </Box>

        {/* Verify Button */}
        <Button
          variant="contained"
          fullWidth
          onClick={handleVerify}
          disabled={otp.join("").length < 4 || loading}
          sx={{
            height: 54,
            borderRadius: "16px",
            textTransform: "none",
            fontWeight: 700,
            fontSize: "1rem",
            letterSpacing: "0.01em",
            background: "linear-gradient(135deg, #1565c0 0%, #0f4fa8 100%)",
            boxShadow: "0 10px 24px rgba(21,101,192,0.2)",
            "&:hover": {
              background: "linear-gradient(135deg, #0f5ab2 0%, #0b4695 100%)",
              boxShadow: "0 12px 28px rgba(21,101,192,0.28)",
            },
            mb: 3,
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Verify & Proceed"}
        </Button>

        {/* Resend Timer section */}
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
          {timer > 0 ? (
            <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
              Resend OTP in <span style={{ color: "#1565c0", fontWeight: 700 }}>{timer}s</span>
            </Typography>
          ) : (
            <Button
              variant="text"
              onClick={handleResend}
              disabled={loading}
              startIcon={<LockReset />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: "#1565c0",
                "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.04)" },
              }}
            >
              Resend OTP
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default OtpPopup;
